import {
  getSessionData,
  getVersionedCallbackUrl,
  getUtmParamsFromCookie,
  redirectToWebform,
} from "@repo/shared-utils/common-utils";
import { decryptWebform } from "@repo/shared-utils/crypto";
import { getOrDefaultValue } from "@repo/shared-utils/insert-params";
import { isOpenInApp } from "@repo/shared-utils/pwa-utils";
import {
  solarInsert,
  solarRead,
  type ISolarInsertApiRequest,
  type ISolarReadLoanDetails,
} from "@repo/connection-manager";
import type { SSORedirectHandler } from "../types/SSO.types";

type SolarFormInsertData = Partial<ISolarInsertApiRequest> & {
  cta?: string;
  sourceUrl?: string;
  retryCount?: string;
};

type DecryptedReqData = {
  ssoRequest?: {
    form?: {
      applicationID?: string;
      mobileNo?: string;
    };
  };
};

const SOLAR_FORM_HANDLER_LOG = "[SolarSSO][FormHandler]";

const getReqDataForm = async (): Promise<{ applicationID?: string; mobileNo?: string }> => {
  const reqData = new URLSearchParams(window.location.search).get("reqData");
  if (!reqData) {
    return {};
  }

  try {
    const decrypted = await decryptWebform(decodeURIComponent(reqData));
    const parsed = JSON.parse(decrypted) as DecryptedReqData;

    return {
      applicationID: parsed?.ssoRequest?.form?.applicationID,
      mobileNo: parsed?.ssoRequest?.form?.mobileNo,
    };
  } catch (error) {
    console.error(`${SOLAR_FORM_HANDLER_LOG} failed to parse reqData`, error);
    return {};
  }
};

const getPrimaryLoan = (readResponse: { solarLoanDetailsList: ISolarReadLoanDetails[] | null }) =>
  readResponse?.solarLoanDetailsList?.[0];

export const solarFormHandler: SSORedirectHandler = async (data) => {
  console.log(`${SOLAR_FORM_HANDLER_LOG} invoked`, {
    module: data.mapping?.ssoModule,
    href: data.mapping?.fullUrl,
  });

  const { mobileNo: sessionMobileNo, custType } = await getSessionData();
  const solarFormInsertData = (data.data?.solarFormInsert ?? {}) as SolarFormInsertData;
  const { applicationID, mobileNo: reqDataMobileNo } = await getReqDataForm();
  const mobileNo = reqDataMobileNo || sessionMobileNo;
  const callbackUrl = await getVersionedCallbackUrl(data.mapping);

  if (!applicationID || !mobileNo) {
    console.log(`${SOLAR_FORM_HANDLER_LOG} missing reqData identifiers`, {
      applicationID,
      mobileNoPresent: Boolean(mobileNo),
    });

    console.warn(`${SOLAR_FORM_HANDLER_LOG} missing applicationID/mobileNo; redirecting directly`);
    await redirectToWebform(callbackUrl.toString(), {
      applicationID,
      mobileNo,
    });
    return;
  }

  console.info(`${SOLAR_FORM_HANDLER_LOG} start`, {
    applicationID,
    mobileNoPresent: Boolean(mobileNo),
    mappingModule: data.mapping?.ssoModule,
  });

  const readResponse = await solarRead({
    id: applicationID,
    mobileNo,
  });
  const loan = getPrimaryLoan(readResponse);
  const utm = getUtmParamsFromCookie();

  const insertResponse = await solarInsert({
    ...solarFormInsertData,
    id: applicationID,
    mobileNo,
    custType,
    journeyName: loan?.journeyName ?? "SRL_NTB",
    eventCode: "WHEELS_CONVERT",
    eventName: "PERSONAL_DETAILS",
    prodName: loan?.prodName ?? "SRL",
    appVersion: "v1",
    saveForLaterFlag: true,
    pwaVersion: "5.1.9",
    sourceSystem: isOpenInApp() ? "3in1App" : "3in1Web",
    sourceChannel: "3in1 App",
    srcChannel: "3in1",
    apiVersion: "P.11.1.4",
    utmSource: getOrDefaultValue(utm?.utmSource),
    utmContent: getOrDefaultValue(utm?.utmContent),
    utmCampaign: getOrDefaultValue(utm?.utmCampaign),
    utmMedium: getOrDefaultValue(utm?.utmMedium),
    offerId: loan?.offerId,
    offerName: loan?.offerName,
    offerAmt: loan?.offerPreApprovedAmount,
  });

  await redirectToWebform(callbackUrl.toString(), {
    applicationID: insertResponse?.id ?? applicationID,
    mobileNo,
  });

  console.info(`${SOLAR_FORM_HANDLER_LOG} complete`, {
    applicationID: insertResponse?.id ?? applicationID,
  });
};
